/**
 * Visit-data tab: renders per-medication chart tables from `visit_data_charts`
 * on auth-on-file, with a sticky header while scrolling.
 *
 * @module visitData
 */
import { useEffect, useRef, useState } from "react";
import NoData from "../../../../components/noData/noData";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import {
  VisitDataChart,
  VisitDataRow,
} from "../../../../data-model/medicalPaOrdersModel";
import { useContainerRect } from "../../../../hooks/useContainerRect";
import { useShowStickyBar } from "./hooks/useShowStickyBar";

/**
 * Converts a row's `Date` cell into a sortable timestamp.
 *
 * @param row - Visit data row whose date should be parsed
 * @returns Milliseconds since epoch, or `Infinity` when the date is missing/unparseable
 */
const getRowTimestamp = (row: VisitDataRow): number => {
  const rawDate = typeof row.Date === "string" ? row.Date.trim() : "";
  const timestamp = rawDate ? new Date(rawDate).getTime() : Number.NaN;

  return Number.isNaN(timestamp) ? Infinity : timestamp;
};

/**
 * Orders chart rows chronologically, keeping undated rows at the bottom.
 *
 * @param rows - Visit data rows as returned by the API
 * @returns New array sorted by ascending date
 */
const sortRowsByDateAscending = (rows: VisitDataRow[]): VisitDataRow[] =>
  [...rows].sort(
    (first, second) => getRowTimestamp(first) - getRowTimestamp(second),
  );

/**
 * Reads `visit_data_charts` from accumulated auth-on-file and lists each chart.
 *
 * @returns Visit data UI with empty state when no charts exist
 */
const VisitData = () => {
  const { authOnFileDetails } = useDataAccumulatorForSingleOrder();
  const [visitDataCharts, setVisitDataCharts] = useState<VisitDataChart[]>([]);
  const visitDatChartReference = useRef<HTMLDivElement>(null);
  const { stickyBarDetails } = useShowStickyBar(visitDatChartReference);
  const { containerRect } = useContainerRect(visitDatChartReference);

  useEffect(() => {
    if (authOnFileDetails?.auth_on_file) {
      const authOnFile = authOnFileDetails.auth_on_file as any;
      const charts: VisitDataChart[] = authOnFile.visit_data_charts ?? [];
      setVisitDataCharts(charts);
    }
  }, [authOnFileDetails]);

  /**
   * @param chart - Full chart entry for one medication (includes nested data)
   * @returns Markup for a single scrollable table block
   */
  const renderTable = (chart: VisitDataChart) => {
    const { headers, data } = chart.data;
    const sortedData = sortRowsByDateAscending(data ?? []);

    return (
      <div key={chart.medicine_name} className="mb-6">
        <h4 className="text-md mb-3 font-semibold text-gray-800">
          {chart.medicine_name}
        </h4>
        <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {headers.map((header) => (
                    <th
                      key={header}
                      className="border-b px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedData.map((row, index) => (
                  <tr
                    key={index}
                    className={
                      row.is_current_period
                        ? "bg-blue-50"
                        : index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                    }
                  >
                    {headers.map((header) => (
                      <td
                        key={header}
                        className="border-b px-4 py-3 text-sm text-gray-900"
                      >
                        {row[header] || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500">
            Total Rows: {chart.total_rows} | Extracted At:{" "}
            {new Date(chart.extracted_at).toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {stickyBarDetails && stickyBarDetails.top < 60 && (
        <div
          className="fixed top-12 z-50 animate-slideDown border-b border-gray-200 bg-white p-4 font-semibold shadow-lg"
          style={{
            left: containerRect?.left ?? 0,
            width: containerRect?.width ?? "auto",
            right: containerRect?.right ?? 0,
          }}
        >
          {stickyBarDetails.chartName}
        </div>
      )}
      <div
        className="visit-data-container min-h-0 flex-1 overflow-y-auto"
        ref={visitDatChartReference}
      >
        {visitDataCharts.length > 0 ? (
          <>
            {visitDataCharts.map((chart) => (
              <div
                key={chart.medicine_name}
                id="chart"
                data-chart-name={chart.medicine_name}
              >
                {renderTable(chart)}
              </div>
            ))}
          </>
        ) : (
          <div className="flex h-full items-center justify-center rounded-lg bg-gray-50">
            <NoData />
          </div>
        )}
      </div>
    </>
  );
};

export default VisitData;
